import React, { useEffect, useState } from 'react';
import { Button, Checkbox, RadioButton } from 'components';
import Modal from 'components/Modal/Modal';
import Typography from 'components/Typography/Typography';
import theme from 'themes';
import { ModalSize } from 'types';
import { useTranslation } from 'react-i18next';
import {
  ButtonBox,
  Content,
  DayFieldConatiner,
  DayFieldWithCheckbox,
  FormContorl,
  FormControlDiv,
  FormGroup,
  InputField,
  LabelField,
  LastTotalAverageHoursConatiner,
  ModalContainer,
  ModalHeader,
  RadioGroup,
  SetCustomValueContainer,
  SetCustomValueContainerFields,
  SetTargetValueContainer,
  SetTargetValueContainerFields,
  ShowHideServicesContainer,
  SmallText,
  Tab,
  Tabs,
  TagetValueStatusConatiner,
  TargetOuterContainer
} from './LineTargetModal.elements';
import { MachineStateTargetValueData } from 'types/protein';
import FileUpload from 'common/components/FileUpload';

interface WeeklyTargetInputsProps {
  totalHours: number;
  setTotalHours: (value: number) => void;
  avgHours: number;
  setAvgHours: (value: number) => void;
  handleServiceChange: (key: 'useCustomDays', value: boolean) => void;
  serviceData: {
    useCustomDays?: boolean;
  };
  disabled?: boolean;
}

interface TargetInputsProps {
  disabled?: boolean;
  customDaySelections: Record<string, boolean>;
  onCheckboxChange: (day: string, checked: boolean) => void;
  onInputChange: (day: string, value: number) => void;
  customDayValues: Record<string, number>;
  serviceData: {
    days?: Record<string, number>;
  };
}

interface CustomTargetInputsProps {
  handleServiceChange: (key: string, value: boolean) => void;
  serviceData: {
    useCustomDays?: boolean;
    days?: Record<string, number>;
  };
  customDaySelections: Record<string, boolean>;
  onCheckboxChange: (day: string, checked: boolean) => void;
  onInputChange: (day: string, value: number) => void;
  customDayValues: Record<string, number>;
}

interface TargetStatusProps {
  handleServiceChange: (key: string, value: boolean) => void;
  serviceData: {
    enabled?: boolean;
  };
}

interface TargetConfig {
  category?: string;
  totalHoursPerWeek?: number;
  enabled?: boolean;
  useCustomDays?: boolean;
  isVisible?: boolean;
  days?: Record<string, number>;
  averagePerDay?: number;
}

interface LineTargetModalProps {
  isOpen: boolean;
  onRequestClose: () => void;
  activeTab: string;
  onSubmitBtn?: (data: { targetConfigs: Record<string, TargetConfig> }) => Promise<void>;
  targetData?: MachineStateTargetValueData[];
  handleFileChange?: (file: File) => void;
  uploadImage?: () => Promise<void>;
}

interface TotalHoursPerTabProps {
  running: number;
}

type DayName = (typeof dayNames)[number];
type ActivityType = 'running';

const dayNames = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

const TargetInputs: React.FC<TargetInputsProps> = ({
  disabled,
  customDaySelections,
  onCheckboxChange,
  onInputChange,
  customDayValues,
  serviceData
}) => {
  return (
    <>
      {dayNames.map((day) => {
        const dayValue = serviceData?.days?.[day] ?? 0;
        const isChecked = customDaySelections[day];
        const isInputEnabled = isChecked && !disabled;
        return (
          <DayFieldConatiner key={day}>
            <DayFieldWithCheckbox>
              <Checkbox
                width={18}
                height={18}
                checked={isChecked}
                onChange={(e) => onCheckboxChange(day, e.target.checked)}
                disabled={disabled && dayValue <= 0}
                label={day.charAt(0).toUpperCase() + day.slice(1)}
                id={`target-${day}`}
                fontSize={14}
              />
            </DayFieldWithCheckbox>
            <InputField
              type="number"
              id={`target-${day}`}
              min="0"
              max="24"
              value={customDayValues?.[day] ?? dayValue}
              onChange={(e) => onInputChange(day, Number(e.target.value))}
              disabled={!isInputEnabled}
            />
          </DayFieldConatiner>
        );
      })}
    </>
  );
};

const WeeklyTargetInputs: React.FC<WeeklyTargetInputsProps> = ({
  totalHours,
  setTotalHours,
  avgHours,
  setAvgHours,
  handleServiceChange,
  serviceData,
  disabled
}) => {
  const { t } = useTranslation(['mh']);
  return (
    <SetTargetValueContainer>
      <RadioButton
        label={t('Set target value (Monday to Sunday)') as string}
        name="select_option_for_target"
        id="select_target_value"
        labelWeight={600}
        onChange={() => handleServiceChange('useCustomDays', false)}
        checked={!serviceData?.useCustomDays}
      />
      <SetTargetValueContainerFields>
        <FormControlDiv>
          <LabelField>
            <span>{t('Set total hours per week') as string}</span>
          </LabelField>
          <InputField
            type="number"
            id="target-weekly"
            min="0"
            max="168"
            value={!serviceData?.useCustomDays ? totalHours : 0}
            onChange={(e) => setTotalHours(Number(e.target.value))}
            disabled={disabled || serviceData?.useCustomDays}
          />
        </FormControlDiv>
        <FormControlDiv orText>{t('OR')}</FormControlDiv>
        <FormControlDiv>
          <LabelField>
            <span>{t('Average hours per day') as string}</span>
          </LabelField>
          <InputField
            type="number"
            id="target-daily"
            min="0"
            max="24"
            value={!serviceData?.useCustomDays ? avgHours.toFixed(2) : 0}
            onChange={(e) => setAvgHours(Number(e.target.value))}
            disabled={disabled || serviceData?.useCustomDays}
          />
        </FormControlDiv>
      </SetTargetValueContainerFields>
    </SetTargetValueContainer>
  );
};

const CustomTargetInputs: React.FC<CustomTargetInputsProps> = ({
  handleServiceChange,
  serviceData,
  customDaySelections,
  onCheckboxChange,
  onInputChange,
  customDayValues
}) => {
  const isCustom = serviceData?.useCustomDays;

  const selectedDaysWithValues = dayNames.filter(
    (day) => customDaySelections[day] && Number(customDayValues[day]) > 0
  );

  const totalHours = selectedDaysWithValues.reduce(
    (sum, day) => sum + Number(customDayValues[day]),
    0
  );

  const avgHours =
    selectedDaysWithValues.length > 0 ? totalHours / selectedDaysWithValues.length : 0;

  const { t } = useTranslation(['mh']);

  return (
    <SetCustomValueContainer>
      <RadioButton
        label={t('Or set custom values for specific days') as string}
        name="select_option_for_target"
        labelWeight={600}
        id="select_custom_target"
        onChange={() => handleServiceChange('useCustomDays', true)}
        checked={isCustom}
      />
      <SetCustomValueContainerFields>
        <TargetInputs
          disabled={!isCustom}
          customDaySelections={customDaySelections}
          onCheckboxChange={onCheckboxChange}
          onInputChange={onInputChange}
          customDayValues={customDayValues}
          serviceData={serviceData}
        />
        <LastTotalAverageHoursConatiner>
          <p>
            <strong>
              {t('Total hours per week')}: {totalHours ?? 0}h
            </strong>
          </p>
          <p>
            <strong>
              {t('Average hours per day')}: {avgHours.toFixed(1) ?? 0}h
            </strong>
          </p>
        </LastTotalAverageHoursConatiner>
      </SetCustomValueContainerFields>
    </SetCustomValueContainer>
  );
};

const TargetStatus = ({ handleServiceChange, serviceData }: TargetStatusProps) => {
  const { t } = useTranslation(['mh']);
  return (
    <TagetValueStatusConatiner>
      <FormContorl>
        <LabelField className="label-space">
          <span>{t('Target value status')}</span>
        </LabelField>
        <RadioGroup>
          <RadioButton
            checked={serviceData?.enabled === true}
            onChange={() => handleServiceChange('enabled', true)}
            label="Enable"
            id="target-status-enable"
            labelWeight={400}
          />
          <RadioButton
            checked={serviceData?.enabled === false}
            onChange={() => handleServiceChange('enabled', false)}
            label="Disable"
            id="target-status-disable"
            labelWeight={400}
          />
        </RadioGroup>
      </FormContorl>
    </TagetValueStatusConatiner>
  );
};

const LineTargetModal = ({
  isOpen,
  onRequestClose,
  activeTab: initialActiveTab,
  onSubmitBtn,
  targetData,
  handleFileChange,
  uploadImage
}: LineTargetModalProps): JSX.Element => {
  const { t } = useTranslation(['mh']);
  const [activeTab, setActiveTab] = useState(initialActiveTab);

  function createEmptyDayRecord<T>(value: T): Record<DayName, T> {
    return Object.fromEntries(dayNames.map((day) => [day, value])) as Record<DayName, T>;
  }

  const zeroDayValues = Object.fromEntries(dayNames.map((day) => [day, 0]));

  interface ICustomDaySelections {
    [key: string]: Record<string, boolean>;
  }

  const [customDaySelections, setCustomDaySelections] = useState<ICustomDaySelections>({
    running: Object.fromEntries(dayNames.map((day) => [day, false]))
  });

  type DayValues = Record<DayName, number>;
  type CustomDayValues = Record<ActivityType, DayValues>;

  const [customDayValues, setCustomDayValues] = useState<CustomDayValues>({
    running: createEmptyDayRecord(0)
  });

  interface TargetConfig {
    totalHoursPerWeek?: number;
    averagePerDay?: number;
    days?: Record<DayName, number>;
    [key: string]: unknown;
  }

  type TargetConfigs = Record<string, TargetConfig>;

  const [targetConfigs, setTargetConfigs] = useState<TargetConfigs>({
    running: { days: createEmptyDayRecord(0) }
  });

  const [totalHoursPerTab, setTotalHoursPerTab] = useState<TotalHoursPerTabProps>({
    running: 0
  });

  const [avgHoursPerTab, setAvgHoursPerTab] = useState({
    running: 0
  });

  const defaultDays = Object.fromEntries(dayNames.map((day) => [day, 0]));
  const createCategoryData = (category: string) => ({
    category,
    totalHoursPerWeek: 0,
    enabled: false,
    useCustomDays: false,
    isVisible: false,
    days: { ...defaultDays },
    averagePerDay: 0
  });
  const categories = ['service', 'cleaning', 'running'];
  const isTargetDataValid = Array.isArray(targetData) && targetData.length > 0;
  const targetTabData = isTargetDataValid ? targetData : categories.map(createCategoryData);

  useEffect(() => {
    const running = targetTabData?.find((item) => item.category === 'running');

    setTargetConfigs({
      running: {
        ...running,
        days: running?.days ?? { ...zeroDayValues }
      }
    });

    if (running) {
      setTotalHoursPerTab((prev) => ({ ...prev, running: running.totalHoursPerWeek || 0 }));
      setAvgHoursPerTab((prev) => ({ ...prev, running: running.averagePerDay || 0 }));
    }

    setCustomDaySelections({
      running: Object.fromEntries(
        dayNames.map((day) => [day, (running?.days as Record<DayName, number>)?.[day] > 0])
      ) as Record<DayName, boolean>
    });

    setCustomDayValues({
      running: running?.days || {}
    });
  }, [targetData]);

  const setTotalHours = (tabKey: string, value: number) => {
    setTotalHoursPerTab((prev) => ({ ...prev, [tabKey]: value }));
    setAvgHoursPerTab((prev) => ({ ...prev, [tabKey]: value / 7 }));
    setTargetConfigs((prev) => ({
      ...prev,
      [tabKey]: {
        ...(prev[tabKey] ?? {}),
        totalHoursPerWeek: value,
        averagePerDay: value / 7
      }
    }));
  };

  const setAvgHours = (tabKey: string, value: number) => {
    setAvgHoursPerTab((prev) => ({ ...prev, [tabKey]: value }));
    setTotalHoursPerTab((prev) => ({ ...prev, [tabKey]: value * 7 }));
    setTargetConfigs((prev) => ({
      ...prev,
      [tabKey]: {
        ...prev[tabKey],
        averagePerDay: value,
        totalHoursPerWeek: value * 7
      }
    }));
  };

  const handleTargetChange = (category: string, key: string, value: boolean) => {
    setTargetConfigs((prev) => {
      const updated = {
        ...prev,
        [category]: {
          ...prev[category],
          [key]: value
        }
      };

      if (key === 'useCustomDays') {
        if (value === true) {
          setTotalHoursPerTab((prevHours) => ({
            ...prevHours,
            [category]: 0
          }));
          setAvgHoursPerTab((prevHours) => ({
            ...prevHours,
            [category]: 0
          }));

          updated[category].totalHoursPerWeek = 0;
          updated[category].averagePerDay = 0;
        } else {
          const clearedSelections = Object.fromEntries(dayNames.map((day) => [day, false]));
          const clearedValues = Object.fromEntries(dayNames.map((day) => [day, 0]));

          setCustomDaySelections((prev) => ({
            ...prev,
            [category]: clearedSelections
          }));
          setCustomDayValues((prev) => ({
            ...prev,
            [category]: clearedValues
          }));

          updated[category].days = { ...clearedValues };
        }
      }

      return updated;
    });
  };

  const handleCustomDayCheckboxChange = (tabKey: string, day: string, checked: boolean) => {
    setCustomDaySelections((prev) => ({
      ...prev,
      [tabKey]: {
        ...prev[tabKey],
        [day]: checked
      }
    }));

    setCustomDayValues((prev) => {
      const updatedValues = {
        ...prev[tabKey as ActivityType],
        [day]: checked ? prev[tabKey as ActivityType][day as DayName] : 0
      };
      return {
        ...prev,
        [tabKey]: updatedValues
      } as CustomDayValues;
    });

    setTargetConfigs((prev) => {
      const currentConfig = prev[tabKey];
      const currentDays = currentConfig.days ?? createEmptyDayRecord(0);

      const updatedDays = {
        ...currentDays,
        [day]: checked ? currentDays[day] : 0
      };

      return {
        ...prev,
        [tabKey]: {
          ...currentConfig,
          days: updatedDays
        }
      };
    });
  };

  const handleCustomDayInputChange = (tabKey: string, day: string, value: number) => {
    setCustomDayValues((prev) => ({
      ...prev,
      [tabKey as ActivityType]: {
        ...prev[tabKey as ActivityType],
        [day as DayName]: value
      }
    }));

    setTargetConfigs((prev) => ({
      ...prev,
      [tabKey]: {
        ...prev[tabKey],
        days: {
          ...prev[tabKey]?.days,
          [day]: value
        }
      }
    }));
  };

  const handleApply = () => {
    if(activeTab === 'lineview' && uploadImage) {
      uploadImage();
    } else if (activeTab === 'running') {
        onSubmitBtn?.({ targetConfigs });
    }
    onRequestClose();
  };

  const renderTab = (key: string, label: string) => (
    <Tab key={key} isActive={activeTab === key} onClick={() => setActiveTab(key)}>
      {t(label)}
    </Tab>
  );

  const renderTargetContent = (
    tabKey: keyof TotalHoursPerTabProps,
    config: MachineStateTargetValueData
  ) => (
    <TargetOuterContainer>
      <WeeklyTargetInputs
        totalHours={totalHoursPerTab[tabKey]}
        setTotalHours={(value) => setTotalHours(tabKey, value)}
        avgHours={avgHoursPerTab[tabKey]}
        setAvgHours={(value) => setAvgHours(tabKey, value)}
        handleServiceChange={(key, value) => handleTargetChange(tabKey, key, value)}
        serviceData={config}
        disabled={config?.useCustomDays}
      />
      <CustomTargetInputs
        handleServiceChange={(key, value) => handleTargetChange(tabKey, key, value)}
        serviceData={config}
        customDaySelections={customDaySelections[tabKey]}
        onCheckboxChange={(day, checked) => handleCustomDayCheckboxChange(tabKey, day, checked)}
        onInputChange={(day, value) => handleCustomDayInputChange(tabKey, day, value)}
        customDayValues={customDayValues[tabKey]}
      />
      <TargetStatus
        handleServiceChange={(key, value) => handleTargetChange(tabKey, key, value)}
        serviceData={config}
      />
    </TargetOuterContainer>
  );

  return (
    <Modal visible={isOpen} onClose={onRequestClose} size={ModalSize.MEDIUM}>
      <ModalHeader>
        <Typography as="h3" mb={2} size="1.125rem" weight="bold">
          {t('configure')}
        </Typography>
        <SmallText>{t('configure_target_values_sub_text')}</SmallText>
      </ModalHeader>
      <ModalContainer>
        <Tabs>
          {renderTab('running', 'running_widget')}
          {renderTab('lineview', 'line_view')}
        </Tabs>
        <Content>
          <FormGroup>
            {activeTab === 'running' && renderTargetContent('running', targetConfigs.running)}
            {activeTab === 'lineview' && 
                <FileUpload
                    className="image-selector"
                    acceptedTypes={{
                    'application/image': ['.jpeg', '.webp', '.png', '.jpg']
                    }}
                    acceptedFileText=".jpg, .png, File size (10 MB max)."
                    maxFileSize={10485760}
                    onFileChange={(file) => {
                    if (file && handleFileChange) {
                        handleFileChange(file);
                    }
                    }}
                />
            }
          </FormGroup>
        </Content>
        <ButtonBox>
          <Button
            width="6.688rem"
            height="2rem"
            borderRadius="3rem"
            borderColor={theme.colors.donutChart.target}
            onClick={onRequestClose}
            color={theme.colors.donutChart.target}
          >
            {t('cancel')}
          </Button>
          <Button
            width="6.688rem"
            height="2rem"
            borderRadius="3rem"
            bgColor={theme.colors.donutChart.target}
            color="white"
            onClick={handleApply}
          >
            {t('apply')}
          </Button>
        </ButtonBox>
      </ModalContainer>
    </Modal>
  );
};

export default LineTargetModal;
